import React, { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  resetCompanyPassword,
  clearError,
  clearStatus,
} from "../../Redux/companySlice";
import Swal from "sweetalert2";
import { FiEye, FiEyeOff } from "react-icons/fi";

const ResetPasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { loading, error, resetPasswordStatus } = useSelector(
    (state) => state.company
  );

  useEffect(() => {
    // Cleanup on component unmount
    return () => {
      dispatch(clearError());
      dispatch(clearStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (resetPasswordStatus === "success") {
      Swal.fire({
        icon: "success",
        title: "Password Reset Successful",
        text: "Redirecting to login page...",
        timer: 1500,
        showConfirmButton: false,
      }).then(() => {
        navigate("/company/login");
      });
    }
  }, [resetPasswordStatus, navigate]);

  useEffect(() => {
    if (error) {
      Swal.fire({
        icon: "error",
        title: "Reset Failed",
        text: error.message || "Error resetting password. Please try again.",
      });
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      Swal.fire("Error", "Passwords do not match", "error");
      return;
    }

    // Password validation
    const minLength = /.{8,32}/;
    const uppercase = /[A-Z]/;
    const lowercase = /[a-z]/;
    const number = /[0-9]/;
    const specialChar = /[@$!%*?&]/;
    const noSpaces = /^\S*$/;

    if (!minLength.test(password)) {
      Swal.fire(
        "Error",
        "Password must be between 8 to 32 characters long.",
        "error"
      );
      return;
    }
    if (!uppercase.test(password)) {
      Swal.fire(
        "Error",
        "Password must contain at least one uppercase letter.",
        "error"
      );
      return;
    }
    if (!lowercase.test(password)) {
      Swal.fire(
        "Error",
        "Password must contain at least one lowercase letter.",
        "error"
      );
      return;
    }
    if (!number.test(password)) {
      Swal.fire("Error", "Password must contain at least one number.", "error");
      return;
    }
    if (!specialChar.test(password)) {
      Swal.fire(
        "Error",
        "Password must contain at least one special character (@, $, !, %, *, ?, &).",
        "error"
      );
      return;
    }
    if (!noSpaces.test(password)) {
      Swal.fire("Error", "Password should not contain spaces.", "error");
      return;
    }

    dispatch(
      resetCompanyPassword({
        token,
        passwords: { newPassword: password, confirmPassword },
      })
    );
  };

  return (
    <div className="flex items-center justify-center flex-col gap-4 h-[calc(100dvh-80px)] bg-gray-100 px-2 md:px-4 overflow-hidden">
      {/* <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-orange-500">
        Infuber
      </h1> */}
      <div className="bg-white p-2 md:p-4 rounded-xl shadow-lg w-full max-w-sm sm:max-w-md lg:max-w-xl">
        <div className="text-center mb-6">
          <h5 className="mt-2 text-gray-700 font-bold text-xs md:text-sm">
            🔐 Set Your New Password
          </h5>
          <p className="text-xs md:text-sm text-gray-500">
            Enter a new password and confirm it to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className="relative flex items-center">
              <Lock className="absolute left-3 w-4 h-4 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full pl-10 p-2 border rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
              <span
                className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <div className="relative flex items-center">
              <Lock className="absolute left-3 w-4 h-4 text-gray-400" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full pl-10 p-2 border rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
              <span
                className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-gray-600"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition text-xs md:text-sm ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Setting New Password..." : "Set New Password"}
          </button>
        </form>

        <p className="text-xs md:text-sm text-gray-500 text-center mt-4 font-medium">
          <Link to="/company/login" className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200">
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
